import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
} from 'reactstrap';
import Gantt from 'frappe-gantt';
import moment from 'moment';

// ---------- helpers ----------
const dateToYMD = (value) => {
  if (!value) return new Date().toISOString().slice(0, 10);
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toISOString().slice(0, 10);
};

const buildTasks = (activities) =>
  activities.map((a) => {
    const id = String(a.id ?? a.hashid ?? Math.random().toString(36).slice(2, 9));
    const name = a.name ?? a.title ?? 'Sin nombre';
    const start = dateToYMD(a.start_date ?? a.start_at ?? a.start);
    const end   = dateToYMD(a.deadline ?? a.end_date ?? a.end);
    const progress = Math.round(Number(a.progress ?? 0));
    const task = { id, name, start, end, progress };
    if (a.risk_level?.name) {
      task.custom_class = `risk-${a.risk_level.name.toLowerCase().replace(/\s+/g, '-')}`;
    }
    return task;
  });

const gantt_container_height_px = () => {
  if (typeof window === 'undefined') return 560;
  const vh = window.innerHeight;
  return Math.max(480, Math.min(900, Math.round(vh * 0.90) - 180));
};

const ActivityGanttModal = ({ activities = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [leftBodyPx, setLeftBodyPx] = useState(475);

  const containerRef = useRef(null);
  const tableScrollRef = useRef(null);

  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const tasks = buildTasks(activities);

  const initGantt = useCallback(() => {
    if (!containerRef.current || tasks.length === 0) return;

    containerRef.current.innerHTML = '';

    try {
      new Gantt(containerRef.current, tasks, {
        view_mode: 'Month',
        language: 'es',
        readonly: true,
        container_height: gantt_container_height_px(),
        bar_height: 22,
        padding: 14,
        upper_header_height: 45,
        lower_header_height: 30,
        custom_popup_html: null,
      });
    } catch (e) {
      console.error('[ActivityGanttModal] gantt init error', e);
    }
  }, [tasks]);

  useEffect(() => {
    if (!isOpen) {
      if (containerRef.current) containerRef.current.innerHTML = '';
      return;
    }

    // Si el usuario cierra el modal pronto, ignorar lo que aún no corrió.
    let cancelled = false;
    // Aquí apuntamos cómo borrar el observer y el listener (cuando ya existan).
    let teardown = () => {};

    // Dar tiempo a que el modal se muestre; luego crear el Gantt.
    const initTimer = setTimeout(() => {
      if (!cancelled) initGantt();
    }, 100);

    // Un poco después que el Gantt ya tiene su contenedor; ahí unimos tabla y scroll.
    const bindTimer = setTimeout(() => {
      if (cancelled || tasks.length === 0) return;
      const ganttEl = containerRef.current?.querySelector('.gantt-container');
      const leftEl = tableScrollRef.current;

      // En móvil la tabla está oculta (CSS): no enlazar scroll ni altura.
      const sidebarHidden = typeof window !== 'undefined' && window.matchMedia('(max-width: 767.98px)').matches;
      if (!ganttEl || !leftEl || sidebarHidden) return;

      // Tabla izquierda: misma altura útil que las filas del Gantt (sin la cabecera).
      const matchBodyHeight = () => {
        setLeftBodyPx(Math.max(120, ganttEl.clientHeight - 85));
      };
      matchBodyHeight();

      // Si el cuadro del Gantt crece o achica, volver a medir.
      const resizeObserver = new ResizeObserver(matchBodyHeight);
      resizeObserver.observe(ganttEl);

      // Scroll del Gantt es el mismo que en la tabla.
      const syncScroll = () => {
        leftEl.scrollTop = ganttEl.scrollTop;
      };
      ganttEl.addEventListener('scroll', syncScroll);

      teardown = () => {
        resizeObserver.disconnect();
        ganttEl.removeEventListener('scroll', syncScroll);
      };
    }, 150);

    // Salir: cancelar los delays y quitar observer + listener.
    return () => {
      cancelled = true;
      clearTimeout(initTimer);
      clearTimeout(bindTimer);
      teardown();
    };
  }, [isOpen, initGantt, tasks.length]);

  // Espacio al final para que la tabla pueda hacer scroll hasta donde el Gantt.
  const fillerPx = Math.max(0, leftBodyPx - tasks.length * 36);

  // Rueda sobre la tabla para mover el scroll del Gantt.
  const onTableWheel = useCallback((event) => {
    const ganttEl = containerRef.current?.querySelector('.gantt-container');
    if (!ganttEl) return;
    ganttEl.scrollTop += event.deltaY;
    event.preventDefault();
  }, []);

  return (
    <>
      <Button
        className="ml-2 py-1"
        type="button"
        color="primary"
        onClick={toggle}
        title="Ver Gantt"
      >
        <i className="fas fa-stream" aria-hidden />
      </Button>

      <Modal isOpen={isOpen} toggle={toggle} size="xl" unmountOnClose={false} style={{ maxWidth: '96vw', width: '96vw' }} >
        <ModalHeader toggle={toggle}>Gantt de actividades</ModalHeader>

        <ModalBody>
          {tasks.length === 0 && (
            <Alert color="warning">No hay actividades para mostrar.</Alert>
          )}

          {tasks.length > 0 && (
            <div className="activity-gantt-modal-layout">
              <div className="activity-gantt-modal-sidebar">
                <div className="activity-gantt-modal-header-row text-center" role="row">
                  <div role="columnheader">Actividad</div>
                  <div role="columnheader">Inicio</div>
                  <div role="columnheader">Fin</div>
                </div>

                <div
                  ref={tableScrollRef}
                  style={{ height: leftBodyPx, overflow: 'hidden' }}
                  onWheel={onTableWheel}
                >
                  <table className="activity-gantt-modal-table">
                    <colgroup>
                      <col />
                      <col className="activity-gantt-modal-col-narrow" />
                      <col className="activity-gantt-modal-col-narrow" />
                    </colgroup>
                    <tbody>
                      {
                        _.map(tasks, (task) => (
                          <tr key={`row-${task.id}`}>
                            <td className="activity-gantt-modal-cell activity-gantt-modal-cell-name">
                              {task.name}
                            </td>
                            <td className="activity-gantt-modal-cell">
                              {moment(task.start).format('DD-MM-YYYY')}
                            </td>
                            <td className="activity-gantt-modal-cell">
                              {moment(task.end).format('DD-MM-YYYY')}
                            </td>
                          </tr>
                        ))
                      }
                      {fillerPx > 0 && (
                        <tr aria-hidden="true">
                          <td colSpan={3} style={{ height: fillerPx, padding: 0, border: 'none' }} />
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="activity-gantt-modal-chart-wrap">
                <div
                  ref={containerRef}
                  id="activity-gantt-modal-container"
                  className="activity-gantt-modal-chart"
                />
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={toggle}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ActivityGanttModal;
